import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { SuiTooltipDirective } from '@semeris/ui';

import { AppActions } from '../../../../store/app/app.actions';
import { AppSelectors } from '../../../../store/app/app.selectors';
import { createPanelToggleGuard } from '../../../shared/utils/panel-toggle-guard';
import { HelpTriggerDirective } from '../../directives/help-trigger.directive';
import { HelpComponentMode } from '../../models/help.model';
import { ProductUpdatesStore } from '../../store/product-updates.store';

/**
 * The navbar `?` button — a toggle, like the Notes button beside it. Extends
 * {@link HelpTriggerDirective} so the open path stays shared; the product-updates dot hangs off
 * this host.
 */
@Component({
  selector: 'semeris-help-icon',
  templateUrl: './help-icon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [SuiTooltipDirective],
})
export class HelpIconComponent extends HelpTriggerDirective {
  private readonly productUpdates = inject(ProductUpdatesStore);

  public readonly helpOpen = this.store.selectSignal(AppSelectors.helpOpen);
  public readonly hasUnseen = this.productUpdates.hasUnseen;

  private readonly canToggle = createPanelToggleGuard();

  public constructor() {
    super();
    this.productUpdates.load();
  }

  public override onOpen(): void {
    if (!this.canToggle()) {
      return;
    }

    if (this.helpOpen()) {
      this.store.dispatch(AppActions.ToggleHelp({ open: false, contextual: true }));
      return;
    }

    if (this.hasUnseen()) {
      // Carries the route's article even though Updates opens first: the panel mounts after this
      // dispatch, so its store seeds from state and the Help tab would otherwise fall back to main.html.
      this.store.dispatch(
        AppActions.ToggleHelp({
          open: true,
          helpId: this.resolveHelpId(),
          contextual: this.contextual(),
          helpMode: HelpComponentMode.Updates,
        }),
      );
      return;
    }

    super.onOpen();
  }
}
